// @ts-nocheck
// Render server-fetched chapter prose. Mounted by App.tsx for each
// chapter that has been migrated out of the inline JSX into the
// book_content table.
//
// Renders sanitised HTML via dangerouslySetInnerHTML. The HTML must
// not contain <script>, <iframe>, or event handlers — the
// admin_upsert_chapter_content RPC accepts whatever the migration
// script gives it, so the safety contract lives at the upload site,
// not here.
//
// Widget mounting: each `widget_anchors` entry is a `{id, ...}` that
// the chapter HTML must contain as `<div data-widget-anchor="id" />`.
// At render time we mount the corresponding React widget into the
// matching anchor element via createPortal. The `widgets` prop is a
// map from anchor id → React node, supplied by App.tsx based on
// chapter (e.g. ch4 supplies the past/present table widget).
//
// Section listen buttons: when `onSectionListen` is supplied, we walk
// every `<div class="sc">` in the rendered prose, inject a small
// placeholder div at the top of each, and portal a React button in.
// Replaces the old setInterval(injectMissing, 500) hack — React owns
// the lifecycle so the buttons can't be wiped by subsequent renders.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useChapterContent } from "../lib/useChapterContent";

type Props = {
  chapter: number;
  enabled?: boolean;
  widgets?: Record<string, React.ReactNode>;
  // Fallback to render when this chapter has no server-side content yet
  // (during the migration period). App.tsx passes the existing inline
  // JSX here so unmigrated chapters keep working without a change.
  fallback: React.ReactNode;
  // When supplied, every .sc block in the chapter gets a "🎧 listen
  // from here" button portalled at the top. The callback receives the
  // 0-based section index (which the AudioPlayer + chapter-N-sections.json
  // understand). Omit to suppress the buttons entirely (chapters 1-5).
  onSectionListen?: (sectionIdx: number) => void;
};

// Inline icon-only button matching the spec: phone and desktop both
// show just the 🎧. Styling lives in App.tsx's global stylesheet
// (`.section-listen-btn`).
function SectionListenButton({ onClick, idx, chapter }: {
  onClick: () => void;
  idx: number;
  chapter: number;
}) {
  const label = `האזן לסעיף ${chapter}.${idx + 1}`;
  return (
    <button
      type="button"
      className="section-listen-btn"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <span aria-hidden="true">🎧</span>
    </button>
  );
}

export function ChapterContent({
  chapter,
  enabled = true,
  widgets = {},
  fallback,
  onSectionListen,
}: Props) {
  const state = useChapterContent(chapter, enabled);
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchors, setAnchors] = useState<HTMLElement[]>([]);
  // Per-section listen-button anchors: one placeholder div at the top
  // of each .sc that React portals a button into. Stored as
  // { idx, el } so the portal key can include the index even when the
  // same DOM node briefly disappears and re-appears.
  const [sectionAnchors, setSectionAnchors] = useState<
    Array<{ idx: number; el: HTMLElement }>
  >([]);

  // After the HTML mounts, find every <div data-widget-anchor=…> so
  // we can portal React widgets into them. ALSO insert placeholders at
  // the top of each .sc block for the listen buttons.
  useEffect(() => {
    if (state.status !== "ready" || !containerRef.current) {
      setAnchors([]);
      setSectionAnchors([]);
      return;
    }
    const root = containerRef.current;
    const widgetAnchors = Array.from(
      root.querySelectorAll<HTMLElement>("[data-widget-anchor]")
    );
    setAnchors(widgetAnchors);

    // Only set up listen-button anchors if a handler was provided.
    // Otherwise we don't touch the DOM at all — leaves chapters 1-5
    // pristine since they don't get the buttons.
    if (!onSectionListen) {
      setSectionAnchors([]);
      return;
    }
    const scs = Array.from(root.querySelectorAll<HTMLElement>(".sc"));
    const newSectionAnchors = scs.map((sc, idx) => {
      // The button sits in the section's title row (.sh), rightmost, next
      // to the section icon (Shlomo, 12.10: "תוסיף לשורה"). Sections
      // without a title row keep the old spot at the top of the card.
      const host = sc.querySelector<HTMLElement>(":scope > .sh") || sc;
      let placeholder = host.querySelector<HTMLElement>(
        ":scope > .section-listen-anchor"
      );
      if (!placeholder) {
        placeholder = document.createElement("div");
        placeholder.className = "section-listen-anchor";
        host.insertBefore(placeholder, host.firstChild);
      }
      return { idx, el: placeholder };
    });
    setSectionAnchors(newSectionAnchors);
  }, [state.status, state.content?.version, onSectionListen]);

  if (state.status === "loading") {
    return <div style={{ padding: 40, textAlign: "center", color: "#8B7355" }}>טוען…</div>;
  }
  if (state.status === "error") {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#C62828" }}>
        שגיאה בטעינת הפרק: {state.error}
      </div>
    );
  }
  if (state.status === "empty") {
    // This chapter hasn't been migrated yet — render the legacy inline JSX.
    return <>{fallback}</>;
  }

  return (
    <>
      <div
        ref={containerRef}
        // The book_content RPC returns trusted HTML that was assembled
        // by the migration script (and inspected by Shlomo before
        // upload). No untrusted input ever reaches this surface.
        dangerouslySetInnerHTML={{ __html: state.content.prose_html }}
      />
      {anchors.map((el) => {
        const id = el.getAttribute("data-widget-anchor") ?? "";
        const widget = widgets[id];
        if (!widget) return null;
        return createPortal(widget, el, id);
      })}
      {onSectionListen
        ? sectionAnchors.map(({ idx, el }) =>
            createPortal(
              <SectionListenButton
                idx={idx}
                chapter={chapter}
                onClick={() => onSectionListen(idx)}
              />,
              el,
              `sec-${idx}`,
            ),
          )
        : null}
    </>
  );
}
